import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import MovieCard from '@/components/base/MovieCard';
import Pagination from '@/components/base/Pagination';
import FilterPanel from '@/pages/category/components/FilterPanel';
import { CATEGORIES, type MovieCategory } from '@/types/movie';
import { filterMovies, getMoviesByCategory, paginate, sortMovies, type MovieFilters, type SortKey } from '@/utils/movieQuery';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

const PAGE_SIZE = 8;

const SORT_OPTIONS: { value: SortKey; label: string; icon: string }[] = [
  { value: 'latest', label: '最新发布', icon: 'ri-calendar-line' },
  { value: 'popular', label: '最受欢迎', icon: 'ri-fire-line' },
  { value: 'rating', label: '评分最高', icon: 'ri-star-line' },
];

const DEFAULT_FILTERS: MovieFilters = {
  yearRange: 'all',
  minRating: 0,
  durationRange: 'all',
};

export default function Category() {
  const { type = '' } = useParams();
  const categoryName = decodeURIComponent(type) as MovieCategory;
  const meta = CATEGORIES.find((cat) => cat.key === categoryName);

  const [filters, setFilters] = useState<MovieFilters>(DEFAULT_FILTERS);
  const [sortKey, setSortKey] = useState<SortKey>('latest');
  const [page, setPage] = useState(1);

  useEffect(() => {
    setFilters(DEFAULT_FILTERS);
    setSortKey('latest');
    setPage(1);
  }, [categoryName]);

  useEffect(() => {
    setPage(1);
  }, [filters, sortKey]);

  const results = useMemo(() => {
    const base = meta ? getMoviesByCategory(meta.key) : [];
    return sortMovies(filterMovies(base, filters), sortKey);
  }, [meta, filters, sortKey]);

  const totalPages = Math.max(1, Math.ceil(results.length / PAGE_SIZE));
  const pageItems = paginate(results, page, PAGE_SIZE);

  useDocumentTitle({
    title: `午夜小电影 - ${meta ? meta.label : '影片'} - 精选午夜主题影片`,
    description: meta
      ? `午夜小电影「${meta.label}」分类精选，${meta.description}共收录 ${getMoviesByCategory(meta.key).length} 部影片，支持按年份、评分、时长筛选。`
      : '午夜小电影影片分类页。',
    keywords: meta ? `午夜${meta.label}电影,${meta.label}短片,午夜影视推荐,午夜小电影` : '午夜小电影',
  });

  if (!meta) {
    return (
      <div className="mx-auto flex w-full flex-col items-center justify-center px-4 py-28 text-center">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-500">
          <i className="ri-emotion-sad-line text-3xl"></i>
        </span>
        <h1 className="mt-6 text-xl text-foreground-950">没有找到该分类</h1>
        <p className="mt-2 text-sm text-foreground-600">该分类可能已调整，去首页看看其他午夜影片吧。</p>
        <Link
          to="/"
          className="mt-6 flex h-11 items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 text-sm font-medium text-white transition hover:bg-primary-600"
        >
          <i className="ri-home-4-line"></i>返回首页
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full px-4 py-8 md:px-8 md:py-12">
      <nav className="flex flex-wrap items-center gap-2 text-xs text-foreground-500">
        <Link to="/" className="transition hover:text-primary-400">首页</Link>
        <i className="ri-arrow-right-s-line"></i>
        <span>分类</span>
        <i className="ri-arrow-right-s-line"></i>
        <span className="text-foreground-800">{meta.label}</span>
      </nav>

      <header className="mt-6 flex flex-col gap-4 border-b border-background-300/40 pb-8 md:flex-row md:items-end md:justify-between">
        <div>
          <div className="flex items-center gap-3">
            <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary-500/15 text-primary-400">
              <i className={`${meta.icon} text-2xl`}></i>
            </span>
            <div>
              <h1 className="text-2xl text-foreground-950 md:text-3xl">{meta.label}类午夜电影</h1>
              <p className="mt-1 text-xs tracking-[0.2em] text-foreground-500">{meta.en.toUpperCase()}</p>
            </div>
          </div>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-foreground-600">{meta.description}</p>
        </div>
        <p className="whitespace-nowrap text-sm text-foreground-500">
          共 <span className="font-display text-xl text-accent-500">{results.length}</span> 部影片
        </p>
      </header>

      <div className="mt-6">
        <FilterPanel filters={filters} onChange={setFilters} onReset={() => setFilters(DEFAULT_FILTERS)} />
      </div>

      <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-1 rounded-full bg-background-100 p-1">
          {SORT_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setSortKey(opt.value)}
              className={`flex items-center gap-1.5 whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs transition ${
                sortKey === opt.value
                  ? 'bg-primary-500 text-white'
                  : 'text-foreground-600 hover:text-foreground-950'
              }`}
            >
              <i className={opt.icon}></i>
              {opt.label}
            </button>
          ))}
        </div>
        <p className="text-xs text-foreground-500">第 {page} / {totalPages} 页</p>
      </div>

      {pageItems.length > 0 ? (
        <>
          <div className="mt-6 grid grid-cols-2 gap-4 md:grid-cols-3 md:gap-6 lg:grid-cols-4">
            {pageItems.map((movie) => (
              <MovieCard key={movie.id} movie={movie} showTags />
            ))}
          </div>
          <Pagination current={page} totalPages={totalPages} onChange={setPage} />
        </>
      ) : (
        <div className="mt-10 flex flex-col items-center justify-center rounded-lg border border-background-300/50 bg-background-100 py-20 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200/70 text-foreground-500">
            <i className="ri-inbox-2-line text-3xl"></i>
          </span>
          <h2 className="mt-5 text-lg text-foreground-950">当前筛选条件下无相关影片</h2>
          <p className="mt-2 text-sm text-foreground-600">试着放宽年份、评分或时长条件，或许能发现更多午夜好片。</p>
          <button
            type="button"
            onClick={() => setFilters(DEFAULT_FILTERS)}
            className="mt-6 flex h-11 items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 text-sm font-medium text-white transition hover:bg-primary-600"
          >
            <i className="ri-refresh-line"></i>重置筛选
          </button>
        </div>
      )}
    </div>
  );
}